import React from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import {
  Edit,
  Trash2,
  TrendingUp,
  TrendingDown,
  AlertTriangle,
  Package,
} from 'lucide-react';
import { InventoryItem, InventoryCategory } from '../../types/inventory';

interface InventoryListProps {
  items: InventoryItem[];
  categories: InventoryCategory[];
  loading: boolean;
  onEdit: (item: InventoryItem) => void;
  onDelete: (id: number) => void;
  onAdjustStock: (item: InventoryItem) => void;
}

const InventoryList: React.FC<InventoryListProps> = ({
  items,
  categories,
  loading,
  onEdit,
  onDelete,
  onAdjustStock,
}) => {
  const { t } = useLanguage();

  const getCategoryName = (categoryId?: number) => {
    if (!categoryId) return '-';
    const category = categories.find((c) => c.id === categoryId);
    return category?.name || '-';
  };

  const getStockStatus = (item: InventoryItem) => {
    if (item.quantity_in_stock === 0) {
      return { color: 'text-red-600 bg-red-50', icon: AlertTriangle, text: t('inventory.outOfStock') };
    } else if (item.quantity_in_stock <= item.minimum_stock_level) {
      return { color: 'text-orange-600 bg-orange-50', icon: TrendingDown, text: t('inventory.lowStock') };
    }
    return { color: 'text-green-600 bg-green-50', icon: TrendingUp, text: t('inventory.inStock') };
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="animate-pulse space-y-4">
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="h-16 bg-gray-200 rounded"></div>
          ))}
        </div>
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow p-12 text-center">
        <Package className="w-16 h-16 text-gray-400 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">{t('inventory.noItems')}</h3>
        <p className="text-gray-500">{t('inventory.noItemsDescription')}</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow overflow-hidden">
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                {t('inventory.item')}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                {t('inventory.category')}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                {t('inventory.stock')}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                {t('inventory.value')}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                {t('inventory.status')}
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                {t('common.actions')}
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {items.map((item) => {
              const stockStatus = getStockStatus(item);
              const totalValue = item.quantity_in_stock * item.selling_price;
              
              return (
                <tr key={item.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      <div className="text-sm font-medium text-gray-900">{item.name}</div>
                      <div className="text-sm text-gray-500">SKU: {item.sku}</div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{getCategoryName(item.category_id)}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      <div className="text-sm font-medium text-gray-900">
                        {item.quantity_in_stock} {item.unit_of_measure}
                      </div>
                      <div className="text-xs text-gray-500">
                        Min: {item.minimum_stock_level} | Reorder: {item.reorder_point}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      <div className="text-sm font-medium text-gray-900">
                        ${totalValue.toFixed(2)}
                      </div>
                      <div className="text-xs text-gray-500">
                        @ ${item.selling_price.toFixed(2)}/{item.unit_of_measure}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${stockStatus.color}`}>
                      <stockStatus.icon className="w-3 h-3 mr-1" />
                      {stockStatus.text}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <button
                      onClick={() => onAdjustStock(item)}
                      className="text-blue-600 hover:text-blue-900 mr-3"
                      title={t('inventory.adjustStock')}
                    >
                      <Package className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => onEdit(item)}
                      className="text-indigo-600 hover:text-indigo-900 mr-3"
                      title={t('common.edit')}
                    >
                      <Edit className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => onDelete(item.id)}
                      className="text-red-600 hover:text-red-900"
                      title={t('common.delete')}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default InventoryList;